import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindContextKeyToSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts/selectors';
import { defineMessages, injectIntl } from 'react-intl';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _ from 'lodash';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import { surveysActions } from '../../../../../redux/modules/surveys';
import { asyncRequestContextsActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import moment from 'moment';
import {
  Survey,
  SurveyAutopopulateContainer,
  FullPageContainer,
} from '@lifeomic/phc-web-toolkit/src/components/Surveys';
import { getInProgressResponsesForSurvey } from '@lifeomic/phc-web-toolkit/src/components/Surveys/util/getInProgressResponsesForSurvey';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { platformUserAuthor } from '@lifeomic/phc-web-toolkit/src/util/platformUserLabel';
import { groupActions } from '@lifeomic/phc-web-toolkit/src/redux/modules/group';
import SendSubjectSurveyButton from './SendSubjectSurveyButton';
import { BeginSurveyButton } from './BeginSurveyButton';
import { InfiniteScroll } from '@lifeomic/chroma-react/components/InfiniteScroll';
import { Pill } from '@lifeomic/chroma-react/components/Pill';
import { mergeSurveyResponseAfterCreate } from './surveyResponseCreateSync';

const messages = defineMessages({
  createSurveyResponseFailure: {
    id: 'patientViewer.internalSurveys.createSurveyResponse.failure',
    defaultMessage: 'Failed to create survey response',
  },
  createSurveyResponseSuccess: {
    id: 'patientViewer.internalSurveys.createSurveyResponse.success',
    defaultMessage: 'Successfully submitted survey response',
  },
  title: {
    id: 'patientViewer.internalSurveys.columns.title',
    defaultMessage: 'Title',
  },
  description: {
    id: 'patientViewer.internalSurveys.columns.description',
    defaultMessage: 'Description',
  },
  date: {
    id: 'patientViewer.internalSurveys.columns.date',
    defaultMessage: 'Date',
  },
  inProgress: {
    id: 'patientViewer.internalSurveys.inProgress',
    defaultMessage: 'In progress',
  },
});

const createUserSurveyContextKey = contextKeys.patientViewer.createUserSurvey;
const groupsContextKey = contextKeys.patientViewer.fetchSurveyListGroups;

const {
  isFetchingSelector: isCreatingUserSurveySelector,
  responsesSelector: createdSurveyResponseSelector,
} = bindContextKeyToSelectors(createUserSurveyContextKey);

const { responsesSelector: groupsSelector } =
  bindContextKeyToSelectors(groupsContextKey);

const styles = theme => ({
  loadingContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  surveyContainer: {
    padding: theme.spacing(2),
  },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  titleCell: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
});

class SurveyList extends PureComponent {
  state = {
    focusedSurvey: null,
    focusedSurveyResponse: null,
    isCreated: null,
    isSaveAndExiting: false,
    consumedLaunchKey: null,
  };

  componentDidMount() {
    const { fetchGroups } = this.props;

    const params = { pageSize: 100 };
    fetchGroups({ params, contextKey: groupsContextKey });
    this.consumeLaunchRequest();
  }

  getColumns = (formatMessage, classes) => [
    {
      header: { label: formatMessage(messages.title) },
      cell: {
        content: survey => {
          const hasInProgress =
            getInProgressResponsesForSurvey(this.props.responses, survey)
              .length > 0;

          return (
            <div className={classes.titleCell}>
              <span>{_.get(survey, 'title')}</span>
              {hasInProgress && (
                <Pill
                  color="primary"
                  label={formatMessage(messages.inProgress)}
                />
              )}
            </div>
          );
        },
      },
    },
    {
      header: { label: formatMessage(messages.description) },
      cell: {
        content: survey => _.get(survey, 'description'),
      },
    },
    {
      header: { label: formatMessage(messages.date) },
      cell: {
        content: survey => {
          const date = _.get(survey, 'date');
          if (!date) return '';

          return moment(date).format('LLL');
        },
      },
    },
  ];

  openNewSurvey = survey => {
    const { resetContext } = this.props;
    resetContext(createUserSurveyContextKey);
    this.setState({
      focusedSurvey: survey,
      focusedSurveyResponse: null,
      isCreated: null,
    });
  };

  openExistingSurvey = (survey, response) => {
    const { resetContext } = this.props;
    resetContext(createUserSurveyContextKey);
    this.setState({
      focusedSurvey: survey,
      focusedSurveyResponse: response,
      isCreated: true,
    });
  };

  handleSurveyResponseUpdate = updatedResponse => {
    const subjectId = this.props.patient?.id;
    const withSubject =
      subjectId && updatedResponse && !updatedResponse.subject?.reference
        ? {
            ...updatedResponse,
            subject: { reference: `Patient/${subjectId}` },
          }
        : updatedResponse;
    this.setState({ focusedSurveyResponse: withSubject });
  };

  consumeLaunchRequest = () => {
    const {
      launchSurveyId,
      launchResponseId,
      projectSurveys,
      responses,
      areResponsesReady,
      hasMoreToLoad,
      loadMode,
      onLaunchConsumed,
      isFetchingProjectSurveys,
    } = this.props;
    if (!launchSurveyId || !areResponsesReady) {
      return;
    }
    const launchKey = `${launchSurveyId}:${launchResponseId || ''}`;
    if (this.state.consumedLaunchKey === launchKey) {
      return;
    }
    const survey = (projectSurveys || []).find(
      candidate => candidate.id === launchSurveyId,
    );
    if (!survey) {
      if (isFetchingProjectSurveys) {
        return;
      }
      if (
        hasMoreToLoad &&
        loadMode &&
        (this.launchLoadAttempts || 0) < 15 &&
        !this.launchLoadPending
      ) {
        this.launchLoadAttempts = (this.launchLoadAttempts || 0) + 1;
        this.launchLoadPending = true;
        loadMode();
      }
      return;
    }
    this.launchLoadAttempts = 0;
    this.launchLoadPending = false;
    this.setState({ consumedLaunchKey: launchKey }, () => {
      onLaunchConsumed?.();
      if (launchResponseId) {
        const response = (responses || []).find(
          candidate => candidate.id === launchResponseId,
        );
        if (response) {
          this.openExistingSurvey(survey, response);
          return;
        }
      }
      this.openNewSurvey(survey);
    });
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.isFetchingProjectSurveys &&
      !this.props.isFetchingProjectSurveys
    ) {
      this.launchLoadPending = false;
    }
    this.consumeLaunchRequest();
    const created = this.props.createdUserSurveyResponse;
    if (
      !created?.id ||
      created === prevProps.createdUserSurveyResponse ||
      !this.state.focusedSurvey
    ) {
      return;
    }

    const current = this.state.focusedSurveyResponse;
    const merged = mergeSurveyResponseAfterCreate(current, created);
    if (merged && merged !== current) {
      this.setState({
        focusedSurveyResponse: merged,
        isCreated: true,
      });
    }
  }

  handleSurveyClose = () => {
    this.setState({
      focusedSurvey: null,
      focusedSurveyResponse: null,
      isCreated: null,
      isSaveAndExiting: false,
    });
    this.props.resetResponses();
  };

  handleSurveyComplete = async (isAutoSave, onSaveComplete) => {
    const {
      createSurveyResponse,
      updateSurveyResponse,
      patient,
      intl: { formatMessage },
      resetContext,
    } = this.props;

    const { focusedSurvey, focusedSurveyResponse } = this.state;

    const subjectId = patient.id;

    const upsertSurveyResponse = notification => {
      resetContext(createUserSurveyContextKey);

      if (this.state.isCreated) {
        focusedSurveyResponse.resourceType = 'QuestionnaireResponse';
        focusedSurveyResponse.subject = { reference: `Patient/${subjectId}` };
        focusedSurveyResponse.questionnaire = {
          reference: `Questionnaire/${focusedSurvey.id}`,
        };
        return updateSurveyResponse(
          focusedSurveyResponse,
          createUserSurveyContextKey,
          notification,
        );
      } else {
        // Set this to true immediately to avoid sending email notifications
        // more than once when the response is created
        this.setState({ isCreated: true });
        focusedSurveyResponse.resourceType = 'QuestionnaireResponse';
        focusedSurveyResponse.subject = { reference: `Patient/${subjectId}` };
        focusedSurveyResponse.questionnaire = {
          reference: `Questionnaire/${focusedSurvey.id}`,
        };
        return createSurveyResponse(
          subjectId,
          focusedSurvey.id,
          focusedSurveyResponse,
          createUserSurveyContextKey,
          notification,
        );
      }
    };

    if (isAutoSave) {
      return upsertSurveyResponse().then(onSaveComplete);
    }

    const notification = new NotificationHelper(
      null,
      formatMessage(messages.createSurveyResponseFailure),
    );

    this.setState({ isSaveAndExiting: true });
    await upsertSurveyResponse(notification);
    this.handleSurveyClose();
  };

  getSubjectGroup = () =>
    this.props.groups.find((group = {}) => group.name === 'Subjects') || null;

  render() {
    const {
      patient,
      projectSurveys,
      isFetchingProjectSurveys,
      classes,
      user,
      isCreatingUserSurvey,
      intl: { formatMessage },
      areResponsesReady,
      responsesError,
      mrnConfig,
    } = this.props;

    const { focusedSurvey, focusedSurveyResponse, isSaveAndExiting } =
      this.state;

    return (
      <>
        {!patient ||
        !!focusedSurvey ||
        isSaveAndExiting ||
        isCreatingUserSurvey ||
        (!areResponsesReady && !responsesError) ? (
          <div className={classes.loadingContainer}>
            <DotLoader />
          </div>
        ) : (
          <InfiniteScroll
            hasNextPage={this.props.hasMoreToLoad}
            loading={isFetchingProjectSurveys}
            loadMore={this.props.loadMode}
            scrollOffset={20}
            scrollContainer="parent"
          >
            <TableModule
              config={this.getColumns(formatMessage, classes)}
              data={projectSurveys}
              wrapCellContent
              isLoading={isFetchingProjectSurveys}
              rowActions={survey => (
                <div className={classes.rowActions}>
                  <BeginSurveyButton
                    survey={survey}
                    responses={this.props.responses}
                    areResponsesReady={areResponsesReady}
                    hasResponsesError={responsesError}
                    currentUserId={user?.id}
                    onStartNew={this.openNewSurvey}
                    onContinue={this.openExistingSurvey}
                  />
                  <SendSubjectSurveyButton
                    survey={survey}
                    patient={this.props.patient}
                    subjectGroup={this.getSubjectGroup()}
                  />
                </div>
              )}
            />
          </InfiniteScroll>
        )}
        <FullPageContainer
          title={_.get(focusedSurvey, 'title')}
          isOpen={!!focusedSurvey && !isSaveAndExiting}
          onClose={this.handleSurveyClose}
        >
          <div className={classes.surveyContainer}>
            <SurveyAutopopulateContainer
              questionnaire={focusedSurvey}
              patientId={patient.id}
              renderWithAutopopulateValues={autopopulateUpdates => (
                <Survey
                  patient={patient}
                  mrnConfig={mrnConfig}
                  isReadOnly={false}
                  questionnaire={focusedSurvey}
                  questionnaireResponse={focusedSurveyResponse}
                  onResponseUpdate={this.handleSurveyResponseUpdate}
                  onComplete={this.handleSurveyComplete}
                  nonSubjectAuthor={platformUserAuthor(user)}
                  onClose={this.handleSurveyClose}
                  autopopulateValues={autopopulateUpdates}
                  autoSave={true}
                  templatePreviewEnabled
                  subjectId={patient.id}
                />
              )}
            />
          </div>
        </FullPageContainer>
      </>
    );
  }
}

SurveyList.propTypes = {
  patient: PropTypes.object.isRequired,
  isFetchingProjectSurveys: PropTypes.bool.isRequired,
  projectSurveys: PropTypes.array.isRequired,
  responses: PropTypes.array,
  areResponsesReady: PropTypes.bool,
  responsesError: PropTypes.bool,
  resetResponses: PropTypes.func,
};

SurveyList.defaultProps = {
  responses: [],
  areResponsesReady: true,
  responsesError: false,
  resetResponses: () => {},
};

const mapDispatchToProps = {
  resetContext: asyncRequestContextsActions.resetContext,
  fetchSurveys: surveysActions.fetchSurveys,
  createSurveyResponse: surveysActions.createSurveyResponse,
  updateSurveyResponse: surveysActions.updateSurveyResponse,
  fetchGroups: groupActions.fetchAccountGroups,
};

const mapStateToProps = state => ({
  isCreatingUserSurvey: isCreatingUserSurveySelector(state),
  createdUserSurveyResponse: createdSurveyResponseSelector(state)[0],
  groups: groupsSelector(state),
  user: userSelectors.selectActiveUser(state),
});

export default compose(
  injectIntl,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(SurveyList);
